import { useEffect, useRef } from 'react'
import { useQuerySources } from '@hooks/useQuery'
import { useAppDispatch, useAppSelector } from 'universal-scripts'
import {
  initNewQuery,
  loadQuery,
  selectNode,
  clearSelection,
  startAddingNode,
  startAddingOperation
} from 'src/store/slices/query'
import { FormattedMessage } from 'react-intl'
import { useDragToScroll } from '@hooks/useDragToScroll'
import NodeElement from './NodeElement/NodeElement'
import type { BlueprintNode } from 'src/types/queries'
import NodeButton, { AddOperationButton } from './NodeElement/NodeButton'

import './BuilderArea.css'

interface PipelineTreeProps {
  node: BlueprintNode
  isRowRoot: boolean
  isAddingHere: boolean
  rootNodeId: string
  getNodeTitle: (type: string, id?: string) => string
  selectedNodeId: string | null
  handleAddOperation: (e: React.MouseEvent, nodeId: string) => void
  readOnly: boolean
}

function PipelineTree({
  node,
  isRowRoot,
  isAddingHere,
  rootNodeId,
  getNodeTitle,
  selectedNodeId,
  handleAddOperation,
  readOnly
}: PipelineTreeProps) {
  const dispatch = useAppDispatch()

  if (node.type === 'join') {
    return (
      <>
        {isRowRoot && !readOnly && (
          <AddOperationButton
            active={isAddingHere}
            onClick={(e) => handleAddOperation(e, rootNodeId)}
          />
        )}
        <NodeElement
          nodeId={node.nodeId}
          type={node.type}
          title={getNodeTitle(node.type)}
          isSelected={node.nodeId === selectedNodeId}
          isEditable={isRowRoot && !readOnly}
          onSelect={() => dispatch(selectNode(node.nodeId))}
        />
        <div className="branches-container">
          <div className="branch-group left-branch-group">
            <PipelineTree
              node={node.left}
              isRowRoot={false}
              isAddingHere={false}
              rootNodeId={rootNodeId}
              getNodeTitle={getNodeTitle}
              selectedNodeId={selectedNodeId}
              handleAddOperation={handleAddOperation}
              readOnly={readOnly}
            />
          </div>
          <div className="branch-group right-branch-group">
            <PipelineTree
              node={node.right}
              isRowRoot={false}
              isAddingHere={false}
              rootNodeId={rootNodeId}
              getNodeTitle={getNodeTitle}
              selectedNodeId={selectedNodeId}
              handleAddOperation={handleAddOperation}
              readOnly={readOnly}
            />
          </div>
        </div>
      </>
    )
  }
  return (
    <>
      {isRowRoot && !readOnly && (
        <AddOperationButton
          active={isAddingHere}
          onClick={(e) => handleAddOperation(e, rootNodeId)}
        />
      )}
      <NodeElement
        nodeId={node.nodeId}
        type={node.type}
        title={getNodeTitle(
          node.type,
          node.type === 'dataset' ? node.id : undefined
        )}
        isSelected={node.nodeId === selectedNodeId}
        isEditable={isRowRoot && !readOnly}
        onSelect={() => dispatch(selectNode(node.nodeId))}
      />
      {'source' in node && (
        <PipelineTree
          node={node.source}
          isRowRoot={false}
          isAddingHere={false}
          rootNodeId={rootNodeId}
          getNodeTitle={getNodeTitle}
          selectedNodeId={selectedNodeId}
          handleAddOperation={handleAddOperation}
          readOnly={readOnly}
        />
      )}
    </>
  )
}

export default function BuilderAreaUI({
  readOnly = false
}: {
  readOnly?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  useDragToScroll(containerRef)
  const dispatch = useAppDispatch()

  const blueprint = useAppSelector((state) => state.query.draft.blueprint || [])
  const datasets = useAppSelector((state) => state.query.datasets)
  const selectedNodeId = useAppSelector(
    (state) => state.query.ui.selectedNodeId
  )
  const isAddingNode = useAppSelector((state) => state.query.ui.isAddingNode)
  const addingOperationOver = useAppSelector(
    (state) => state.query.ui.addingOperationOver
  )

  const handleAddNode = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(startAddingNode())
  }

  const handleAddOperation = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation()
    dispatch(startAddingOperation(nodeId))
  }

  const getNodeTitle = (nodeType: string, datasetId?: string) => {
    if (nodeType === 'dataset' && datasetId) {
      const found = datasets?.find((d) => d.id === datasetId)
      return found?.title ?? datasetId
    }
    return nodeType
  }

  return (
    <div
      ref={containerRef}
      id="query-builder-area"
      onClick={() => dispatch(clearSelection())}
    >
      <div className="builder-container">
        <ul className="node-list">
          {blueprint.map((rootNode) => {
            const isAddingHere = addingOperationOver === rootNode.nodeId
            const isJoin = rootNode.type === 'join'

            return (
              <li key={rootNode.nodeId}>
                <div className={`pipeline-row ${isJoin ? 'join-row' : ''}`}>
                  <PipelineTree
                    node={rootNode}
                    isRowRoot={true}
                    isAddingHere={isAddingHere}
                    rootNodeId={rootNode.nodeId}
                    getNodeTitle={getNodeTitle}
                    selectedNodeId={selectedNodeId}
                    handleAddOperation={handleAddOperation}
                    readOnly={readOnly}
                  />
                </div>
              </li>
            )
          })}
        </ul>
        {!readOnly && (
          <NodeButton
            active={isAddingNode}
            className={`node-button dataset-node-button ${isAddingNode ? 'selected' : ''}`}
            onClick={handleAddNode}
          >
            <span className="icon icon-plus" />
            <span>
              <FormattedMessage id="builder-area.button" />
            </span>
          </NodeButton>
        )}
      </div>
    </div>
  )
}

export function BuilderAreaNew() {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(initNewQuery())
  }, [dispatch])

  return <BuilderAreaUI />
}

export function BuilderAreaExisting({
  id,
  readOnly = false
}: {
  id: string
  readOnly?: boolean
}) {
  const dispatch = useAppDispatch()
  const { query, datasets } = useQuerySources(id)
  const isLoaded = useAppSelector((state) => state.query.original?.id === id)

  useEffect(() => {
    if (query && !isLoaded) {
      dispatch(loadQuery({ query, datasets }))
    }
  }, [query, datasets, dispatch, isLoaded])

  return <BuilderAreaUI readOnly={readOnly} />
}
